import RenderEntity, { RENDER_ENTITY_FORMAT3, RENDER_ENTITY_FORMAT6 } from 'ui-component/renders/RenderEntity';
import { lazy } from 'react';

// material-ui
import { Typography, Tooltip } from '@mui/material';

// third-party
import { format, parseJSON } from 'date-fns';

// project imports
import { appState, appGridActions } from 'store/behaviors/loader';
import capabilities from './capabilities';
// import RenderEntityList, { RENDER_ENTITYLIST_FORMAT1 } from 'ui-component/renders/RenderEntityList';
import Avatar from 'ui-component/extended/Avatar';

// assets
import PersonIcon from '@mui/icons-material/Person';
import { renderUser } from 'ui-component/fields/EntitySelect/admin/UserSelect';

// lazy loaded components
const DepartmentSelect = lazy(() => import('ui-component/fields/EntitySelect/common/DepartmentSelect'));
const UserSelect = lazy(() => import('ui-component/fields/EntitySelect/admin/UserSelect'));
// const PhoneNumberSelect = lazy(() => import('ui-component/fields/EntitySelect/common/PhoneNumberSelect'));

const initialState = {
    ...appState(capabilities),
    colDef: [
        {
            field: 'actions',
            type: 'actions',
            description: '',
            align: 'center',
            hideable: false,
            sortable: false,
            resizable: false,
            disableReorder: true,
            label: 'ui-comp-table-actions',
            getActions: (params, permissionData, onClickActions) => [
                ...appGridActions(capabilities, params?.row, permissionData, onClickActions)
            ]
        },
        {
            field: 'id',
            type: 'number',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'traits-appentity-id'
        },
        {
            field: 'avatar',
            type: 'string',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            renderCell: (params) =>
                params?.value ? (
                    <Avatar size="xs" alt="" avatar={params.value} />
                ) : (
                    <Avatar size="xs" alt="">
                        <PersonIcon fontSize="small" />
                    </Avatar>
                ),
            label: 'entity-admin-user-avatar'
        },
        {
            field: 'code',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'traits-appentityhasautogencode-code'
        },
        {
            field: 'name',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'traits-appentityisdescribable-name'
        },
        {
            field: 'lastName',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-common-person-lastname'
        },
        {
            field: 'description',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            flex: 1,
            label: 'traits-appentityisdescribable-description'
        },
        {
            field: 'email',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-common-employee-email'
        },
        {
            field: 'phoneNumbers',
            type: 'singleSelect',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-common-phonenumber-plural'
        },
        {
            field: 'department',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="department"
                    renderPattern="<name>"
                    renderTooltipPattern="<name>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-common-department'
        },
        {
            field: 'user',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="user"
                    renderPattern="<username>"
                    renderTooltipPattern="<username>"
                    renderFormat={RENDER_ENTITY_FORMAT6}
                    {...params}
                />
            ),
            label: 'entity-admin-user'
        },
        {
            field: 'businessOpportunitiesIsResponsibleCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-common-employee-businessopportunitiesisresponsible'
        },
        {
            field: 'businessOpportunitiesIsParticipantCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-common-employee-businessopportunitiesisparticipant'
        },
        {
            field: 'workOrdersIsResponsibleCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-common-employee-workordersisresponsible'
        },
        {
            field: 'workOrdersIsParticipantCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-common-employee-workordersisparticipant'
        },
        {
            field: 'followUpStepsIsResponsibleCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-common-employee-followupstepsisresponsible'
        },
        {
            field: 'createdAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-createdat'
        },
        {
            field: 'createdBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-createdby'
        },
        {
            field: 'updatedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-updatedat'
        },
        {
            field: 'updatedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-updatedby'
        },
        {
            field: 'deletedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            field: 'deletedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentityissafedeletable-deletedby'
        }
    ],
    dataGridState: {
        columns: {
            columnVisibilityModel: {
                id: false,
                avatar: true,
                code: true,
                name: true,
                lastName: true,
                description: false,
                email: true,
                phoneNumbers: true,
                department: true,
                user: true,
                businessOpportunitiesIsResponsibleCount: true,
                businessOpportunitiesIsParticipantCount: true,
                workOrdersIsResponsibleCount: true,
                workOrdersIsParticipantCount: true,
                followUpStepsIsResponsibleCount: true,
                createdAt: true,
                createdBy: true,
                updatedAt: false,
                updatedBy: false,
                deletedAt: false,
                deletedBy: false
            },
            orderedFields: [
                'actions',
                'id',
                'avatar',
                'code',
                'name',
                'lastName',
                'description',
                'email',
                'phoneNumbers',
                'department',
                'user',
                'businessOpportunitiesIsResponsibleCount',
                'businessOpportunitiesIsParticipantCount',
                'workOrdersIsResponsibleCount',
                'workOrdersIsParticipantCount',
                'followUpStepsIsResponsibleCount',
                'createdAt',
                'createdBy',
                'updatedAt',
                'updatedBy',
                'deletedAt',
                'deletedBy'
            ],
            dimensions: {
                actions: { width: 60 },
                id: { width: 100 },
                avatar: { width: 100 },
                code: { width: 100 },
                name: { width: 150 },
                lastName: { width: 150 },
                description: { width: 100 },
                email: { width: 150 },
                phoneNumbers: { width: 100 },
                department: { width: 100 },
                user: { width: 100 },
                businessOpportunitiesIsResponsibleCount: { width: 100 },
                businessOpportunitiesIsParticipantCount: { width: 100 },
                workOrdersIsResponsibleCount: { width: 100 },
                workOrdersIsParticipantCount: { width: 100 },
                followUpStepsIsResponsibleCount: { width: 100 },
                createdAt: { width: 100 },
                createdBy: { width: 100 },
                updatedAt: { width: 100 },
                updatedBy: { width: 100 },
                deletedAt: { width: 100 },
                deletedBy: { width: 100 }
            }
        },
        sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }]
        },
        toolbarFilters: { includeSafeDeleted: false }
    },
    filterDef: [
        {
            filterType: 'string',
            field: { fieldName: 'code', fieldType: 'simple' },
            label: 'traits-appentityhasautogencode-code'
        },
        {
            filterType: 'string',
            field: { fieldName: 'name', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-name'
        },
        {
            filterType: 'string',
            field: { fieldName: 'lastName', fieldType: 'simple' },
            label: 'entity-common-person-lastname'
        },
        {
            filterType: 'string',
            field: { fieldName: 'description', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-description'
        },
        {
            filterType: 'string',
            field: { fieldName: 'email', fieldType: 'simple' },
            label: 'entity-common-employee-email'
        },
        {
            filterType: 'string',
            field: { fieldName: 'phoneNumbers', fieldType: 'simple' },
            label: 'entity-common-phonenumber'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'departmentId', fieldType: 'simple' },
            label: 'entity-common-department',
            renderField: (params) => <DepartmentSelect key="filterEmployee-department" multiple {...params} />
        },
        {
            filterType: 'reference',
            field: { fieldName: 'userId', fieldType: 'simple' },
            label: 'entity-admin-user',
            renderField: (params) => <UserSelect key="filterEmployee-user" multiple {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'createdAt', fieldType: 'simple' },
            label: 'traits-appentity-createdat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'createdBy', fieldType: 'simple', outputFieldName: 'createdById' },
            label: 'traits-appentity-createdby',
            renderField: (params) => <UserSelect key="filterEmployee-createdBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'updatedAt', fieldType: 'simple' },
            label: 'traits-appentity-updatedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'updatedBy', fieldType: 'simple', outputFieldName: 'updatedById' },
            label: 'traits-appentity-updatedby',
            renderField: (params) => <UserSelect key="filterEmployee-updatedBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'deletedAt', fieldType: 'simple' },
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'deletedBy', fieldType: 'simple', outputFieldName: 'deletedById' },
            label: 'traits-appentityissafedeletable-deletedby',
            renderField: (params) => <UserSelect key="filterEmployee-deletedBy" multiple displayMode="fullName" {...params} />
        }
    ],
    quickSearchFilterDef: {
        placeholder: 'ui-comp-quicksearchplaceholder-employee',
        fields: [
            { fieldName: 'code', fieldType: 'simple' },
            { fieldName: 'name', fieldType: 'simple' },
            { fieldName: 'lastName', fieldType: 'simple' },
            { fieldName: 'email', fieldType: 'simple' },
            { fieldName: 'description', fieldType: 'simple' },
            { fieldName: 'departmentName', fieldType: 'simple' },
            { fieldName: 'userUsername', fieldType: 'simple' },
            { fieldName: 'userEmail', fieldType: 'simple' },
            { fieldName: 'userName', fieldType: 'simple' },
            { fieldName: 'userLastName', fieldType: 'simple' }
        ]
    },
    sortingDef: [
        { fieldName: 'id', fieldType: 'simple' },
        { fieldName: 'code', fieldType: 'simple' },
        { fieldName: 'name', fieldType: 'simple' },
        { fieldName: 'lastName', fieldType: 'simple' },
        { fieldName: 'email', fieldType: 'simple' },
        { fieldName: 'departmentId', fieldType: 'simple' },
        { fieldName: 'userId', fieldType: 'simple' },
        { fieldName: 'createdAt', fieldType: 'simple' },
        { fieldName: 'createdBy', fieldType: 'simple' },
        { fieldName: 'updatedAt', fieldType: 'simple' },
        { fieldName: 'updatedBy', fieldType: 'simple' },
        { fieldName: 'deletedAt', fieldType: 'simple' },
        { fieldName: 'deletedBy', fieldType: 'simple' }
    ]
};

export default initialState;
